import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Download, Copy, Check, Loader2, SlidersHorizontal,
  Ruler, TriangleRight, Home, AlertCircle, MapPin, Settings2, Building2, Satellite,
  Layers, Sun, TreePine, Gauge, ShieldCheck, Building, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import MapPreview from "@/components/MapPreview";
import OrderAgainButton from "@/components/OrderAgainButton";
import Seo from "@/components/Seo";

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waste, setWaste] = useState(10);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPdfOpts, setShowPdfOpts] = useState(false);
  const [pdfOpts, setPdfOpts] = useState({ satellite: true, diagram: true, pitch: true, gutters: true, claim: true, business: "" });
  const pollRef = useRef(null);

  useEffect(() => {
    if (user && typeof user === "object" && user.company_name) {
      setPdfOpts((o) => (o.business ? o : { ...o, business: user.company_name }));
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get(`/reports/${id}`);
        if (cancelled) return;
        setReport(data);
        if (["complete", "failed", "cancelled"].includes(data.status) && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        if (!cancelled) toast.error("Couldn't load this report.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    pollRef.current = setInterval(load, 3500);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [id]);

  if (loading) {
    return <div className="container-mtr py-12"><div className="card-mtr h-64 skeleton rounded-card" /></div>;
  }
  if (!report) {
    return (
      <div className="container-mtr py-12 text-center" data-testid="report-not-found">
        <p className="text-iron font-semibold">Report not found.</p>
        <Link to="/reports" className="btn-ghost mt-4 inline-flex">Back to reports</Link>
      </div>
    );
  }

  const m = report.measurements;
  const complete = report.status === "complete" && m;
  const totalSquares = m?.totalSquares || 0;
  const adjustedSquares = +(totalSquares * (1 + waste / 100)).toFixed(1);

  const copyMeasurements = async () => {
    if (!m) return;
    const a = m.attributes || {};
    const lines = [
      `MeasureTheRoof — ${report.address?.full}`,
      `Total area: ${m.totalArea} sq ft (${m.totalSquares} squares)`,
      `Adjusted squares @ ${waste}% waste: ${adjustedSquares}`,
      `Predominant pitch: ${m.predominantPitch} (${m.pitchComplexity} complexity)`,
    ];
    if (a.roofMaterial?.value) lines.push(`Roof material: ${a.roofMaterial.value}`);
    if (a.roofShape?.value) lines.push(`Roof shape: ${a.roofShape.value}`);
    if (a.roofCondition?.length) lines.push(`Roof condition: ${a.roofCondition.map((c) => c.value).join(", ")}`);
    if (a.storyCount) lines.push(`Stories: ${a.storyCount}`);
    if (a.eaveHeight?.min != null) lines.push(`Eave height: ${a.eaveHeight.min}–${a.eaveHeight.max} ${a.eaveHeight.unit || "ft"}`);
    if (a.solarPanel) lines.push(`Solar panels: ${a.solarPanel.value ? "Yes" : "No"}`);
    lines.push(
      `Facets: ${m.facets}`,
      `Ridges: ${m.edges.ridgesFt} ft | Hips: ${m.edges.hipsFt} ft | Valleys: ${m.edges.valleysFt} ft`,
      `Eaves: ${m.edges.eavesFt} ft | Rakes: ${m.edges.rakesFt} ft`,
      `Step flashing: ${m.edges.stepFlashingFt} ft | Flashing: ${m.edges.flashingFt} ft`,
    );
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Measurements copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${report.report_id}/pdf`, {
        params: {
          waste,
          satellite_img: pdfOpts.satellite,
          diagram: pdfOpts.diagram,
          pitch: pdfOpts.pitch,
          gutters: pdfOpts.gutters,
          claim_summary: pdfOpts.claim,
          business_name: pdfOpts.business || "",
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `MeasureTheRoof-${report.report_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="container-mtr py-8 sm:py-12 max-w-4xl" data-testid="report-detail-page">
      <Seo title={`Roof report — ${report.address?.full || ""}`} description="Roof measurement report detail." path={`/reports/${id}`} noindex />
      <button onClick={() => navigate("/reports")} className="text-mortar hover:text-iron flex items-center gap-1.5 text-[14px] mb-4" data-testid="detail-back">
        <ArrowLeft size={16} /> All reports
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-body font-extrabold text-navy text-[24px] sm:text-[28px] flex items-start gap-2">
            <MapPin className="text-orange mt-1 shrink-0" size={22} /> {report.address?.full}
          </h1>
          <p className="text-mortar text-[14px] mt-1">{report.product_name} · ${Number(report.customer_price).toFixed(2)} · {new Date(report.created_at).toLocaleDateString()}</p>
        </div>
        <StatusBadge status={report.status} />
      </div>

      {!complete ? (
        <div className="card-mtr text-center py-12" data-testid="report-processing-state">
          {report.status === "failed" ? (
            <>
              <AlertCircle className="text-caution-red mx-auto mb-3" size={36} />
              <p className="font-bold text-navy text-[18px]">This report failed to generate</p>
              <p className="text-mortar text-[14px] mt-1">Contact <a href="mailto:support@measuretheroof.com" className="text-orange font-semibold">support</a> — we'll help.</p>
            </>
          ) : (
            <>
              <Loader2 className="text-sky-blue animate-spin mx-auto mb-3" size={36} />
              <p className="font-bold text-navy text-[18px]">Your report is being generated</p>
              <p className="text-mortar text-[14px] mt-1">This page updates automatically. We'll also email you when it's ready.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {report.data_source === "mock" && (
            <div className="rounded-input bg-[#FDE8D5] text-orange text-[13px] font-semibold px-4 py-2.5" data-testid="sample-data-notice">
              Sample measurement data (preview mode). Your live property data will appear here once enabled.
            </div>
          )}

          <ClaimBanner attributes={m.attributes} />

          {/* Headline: adjusted squares + waste factor */}
          <div className="card-mtr" data-testid="waste-factor-card">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <span className="label-mtr flex items-center gap-2"><SlidersHorizontal size={14} className="text-orange" /> Waste factor <span className="text-orange ml-auto font-extrabold" data-testid="waste-value">{waste}%</span></span>
                <input
                  type="range" min="5" max="25" step="1" value={waste}
                  onChange={(e) => setWaste(+e.target.value)}
                  className="w-full accent-orange mt-2"
                  data-testid="waste-slider"
                  aria-label="Waste factor percentage"
                />
                <div className="flex justify-between text-[12px] text-mortar mt-1"><span>5%</span><span>25%</span></div>
                <p className="text-[13px] text-mortar mt-3">Materials to order, including cuts and overlaps.</p>
              </div>
              <div className="bg-fieldstone rounded-input p-5 text-center">
                <p className="label-mtr !mb-1">Squares to order</p>
                <p className="metric text-[44px] leading-none text-orange" data-testid="adjusted-squares">{adjustedSquares}</p>
                <p className="text-[13px] text-mortar mt-2">{totalSquares} squares + {waste}% waste</p>
              </div>
            </div>
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Metric icon={Home} label="Total area" value={`${m.totalArea}`} unit="sq ft" testid="metric-total-area" />
            <Metric icon={Ruler} label="Total squares" value={`${m.totalSquares}`} testid="metric-total-squares" />
            <Metric icon={TriangleRight} label="Predominant pitch" value={m.predominantPitch} testid="metric-pitch" />
            <Metric icon={SlidersHorizontal} label="Pitch complexity" value={m.pitchComplexity} testid="metric-complexity" />
          </div>

          {m.attributes && <RoofAttributes a={m.attributes} />}

          <div className="grid md:grid-cols-2 gap-5">
            {/* Edges */}
            <div className="card-mtr">
              <h2 className="font-bold text-navy text-[17px] mb-4">Edge lengths</h2>
              <dl className="grid grid-cols-2 gap-y-3 gap-x-4">
                <Edge label="Ridges" value={m.edges.ridgesFt} />
                <Edge label="Hips" value={m.edges.hipsFt} />
                <Edge label="Valleys" value={m.edges.valleysFt} />
                <Edge label="Rakes" value={m.edges.rakesFt} />
                <Edge label="Eaves" value={m.edges.eavesFt} />
                <Edge label="Ridges + Hips" value={m.edges.ridgesHipsFt} />
                <Edge label="Step flashing" value={m.edges.stepFlashingFt} />
                <Edge label="Flashing" value={m.edges.flashingFt} />
                {m.edges.parapetsFt != null && <Edge label="Parapets" value={m.edges.parapetsFt} />}
              </dl>
              {m.estimated_geometry && (
                <p className="text-[12px] text-mortar mt-3 leading-snug" data-testid="estimated-geometry-note">
                  Edge lengths, facets &amp; gutters are estimated from the measured roof area. Area, pitch
                  and roof attributes are reported by EagleView.
                </p>
              )}
              {m.gutter && (
                <div className="mt-4 pt-4 border-t border-gravel grid grid-cols-2 gap-y-3 gap-x-4">
                  <div>
                    <dt className="text-[12px] uppercase tracking-[0.06em] text-mortar font-semibold">Downspouts (est.)</dt>
                    <dd className="metric text-[18px]">{m.gutter.downspouts}</dd>
                  </div>
                  <div>
                    <dt className="text-[12px] uppercase tracking-[0.06em] text-mortar font-semibold">Max stories</dt>
                    <dd className="metric text-[18px]">{m.gutter.storiesMax}</dd>
                  </div>
                </div>
              )}
            </div>

            {/* Map + pitch breakdown */}
            <div className="space-y-5">
              <div className="card-mtr">
                <h2 className="font-bold text-navy text-[17px] mb-3">Property</h2>
                <MapPreview lat={report.address?.lat} lng={report.address?.lng} height={180} testid="detail-map" />
              </div>
              <div className="card-mtr">
                <h2 className="font-bold text-navy text-[17px] mb-3">Pitch breakdown</h2>
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="text-mortar text-[12px] uppercase tracking-wide text-left">
                      <th className="font-semibold pb-2">Pitch</th><th className="font-semibold pb-2 text-right">Area</th><th className="font-semibold pb-2 text-right">Squares</th>
                    </tr>
                  </thead>
                  <tbody className="tnum">
                    {m.pitchBreakdown.map((p, i) => (
                      <tr key={i} className="border-t border-gravel">
                        <td className="py-2 font-semibold text-navy">{p.pitch}</td>
                        <td className="py-2 text-right text-iron">{p.area} ft²</td>
                        <td className="py-2 text-right text-iron">{p.squares}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* PDF customization */}
          <div className="card-mtr" data-testid="pdf-customize-card">
            <button
              onClick={() => setShowPdfOpts((s) => !s)}
              className="w-full flex items-center justify-between"
              data-testid="pdf-customize-toggle"
            >
              <span className="font-bold text-navy text-[17px] flex items-center gap-2">
                <Settings2 size={18} className="text-orange" /> Customize your PDF
              </span>
              <span className="text-orange text-2xl leading-none">{showPdfOpts ? "−" : "+"}</span>
            </button>
            {showPdfOpts && (
              <div className="mt-4 space-y-4 animate-fade-up" data-testid="pdf-options">
                <div>
                  <label className="label-mtr flex items-center gap-2"><Building2 size={13} /> Your business name (on report)</label>
                  <input
                    className="input-mtr"
                    placeholder="e.g. Smith Roofing LLC"
                    value={pdfOpts.business}
                    onChange={(e) => setPdfOpts((o) => ({ ...o, business: e.target.value }))}
                    data-testid="pdf-business-name"
                    maxLength={80}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <PdfToggle label="Satellite image" icon={Satellite} checked={pdfOpts.satellite} onChange={(v) => setPdfOpts((o) => ({ ...o, satellite: v }))} testid="pdf-opt-satellite" />
                  <PdfToggle label="Roof diagram" icon={Home} checked={pdfOpts.diagram} onChange={(v) => setPdfOpts((o) => ({ ...o, diagram: v }))} testid="pdf-opt-diagram" />
                  <PdfToggle label="Pitch breakdown" icon={TriangleRight} checked={pdfOpts.pitch} onChange={(v) => setPdfOpts((o) => ({ ...o, pitch: v }))} testid="pdf-opt-pitch" />
                  <PdfToggle label="Gutters & structure" icon={Ruler} checked={pdfOpts.gutters} onChange={(v) => setPdfOpts((o) => ({ ...o, gutters: v }))} testid="pdf-opt-gutters" />
                  <PdfToggle label="“Before you go” field notes" icon={ShieldCheck} checked={pdfOpts.claim} onChange={(v) => setPdfOpts((o) => ({ ...o, claim: v }))} testid="pdf-opt-claim" />
                </div>
                <p className="text-[12px] text-mortar">Waste factor ({waste}%) and these options are saved into the downloaded PDF.</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button onClick={downloadPdf} disabled={downloading} className="btn-secondary" data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} Download PDF
            </button>
            <button onClick={copyMeasurements} className="btn-ghost" data-testid="copy-measurements-btn">
              {copied ? <><Check size={18} className="text-go-green" /> Copied</> : <><Copy size={18} /> Copy measurements</>}
            </button>
            <OrderAgainButton reportId={report.report_id} className="btn-primary" label="Order this roof again" testid="reorder-btn" />
          </div>
        </div>
      )}
    </div>
  );
}

function ClaimBanner({ attributes }) {
  const a = attributes || {};
  const conds = (a.roofCondition || []).map((c) => c.value).filter(Boolean);
  if (!conds.length) return null;
  const damageRe = /poor|missing|damage|worn|fair|broken|hail|wind/i;
  const damage = conds.filter((c) => damageRe.test(c));
  if (damage.length) {
    return (
      <div
        className="flex items-start gap-2.5 rounded-input bg-[#FDECEC] border border-[#F3C9C9] text-caution-red px-4 py-3"
        data-testid="claim-banner"
        data-claim="signal"
      >
        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
        <p className="text-[13px] font-semibold leading-snug">
          Claim-ready signal — {damage.join(", ")} detected from aerial imagery. Document this on site to
          support an insurance inspection or claim. (See the "Before you go" page in your PDF.)
        </p>
      </div>
    );
  }
  return (
    <div
      className="flex items-start gap-2.5 rounded-input bg-[#E9F6EC] border border-[#C8E6CF] text-go-green px-4 py-3"
      data-testid="claim-banner"
      data-claim="good"
    >
      <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
      <p className="text-[13px] font-semibold leading-snug">
        Roof condition reported as {conds.join(", ")} — no damage flags detected from aerial imagery.
      </p>
    </div>
  );
}

function PdfToggle({ label, icon: Icon, checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-center justify-between gap-3 p-3 rounded-input border-2 transition-colors ${checked ? "border-orange bg-[#FDF3EA]" : "border-gravel bg-white"}`}
      data-testid={testid}
      role="switch"
      aria-checked={checked}
    >
      <span className="flex items-center gap-2 text-[14px] font-semibold text-iron">
        <Icon size={16} className={checked ? "text-orange" : "text-mortar"} /> {label}
      </span>
      <span className={`w-10 h-6 rounded-full shrink-0 relative transition-colors ${checked ? "bg-orange" : "bg-gravel"}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function Metric({ icon: Icon, label, value, unit, testid }) {
  return (
    <div className="card-mtr !p-4" data-testid={testid}>
      <Icon className="text-orange mb-2" size={20} aria-hidden="true" />
      <p className="text-[11px] uppercase tracking-[0.08em] text-mortar font-semibold">{label}</p>
      <p className="metric text-[24px] mt-0.5">{value}{unit && <span className="text-[13px] text-mortar font-normal ml-1">{unit}</span>}</p>
    </div>
  );
}

function Edge({ label, value }) {
  return (
    <div>
      <dt className="text-[12px] uppercase tracking-[0.06em] text-mortar font-semibold">{label}</dt>
      <dd className="metric text-[18px]">{value} <span className="text-[12px] text-mortar font-normal">ft</span></dd>
    </div>
  );
}

function AttrTile({ icon: Icon, label, value, sub, testid }) {
  if (value == null || value === "") return null;
  return (
    <div className="bg-fieldstone rounded-input p-3.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-mortar">
        <Icon size={14} className="text-orange shrink-0" aria-hidden="true" />
        <span className="text-[11px] uppercase tracking-[0.07em] font-semibold">{label}</span>
      </div>
      <p className="font-bold text-navy text-[16px] mt-1 leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-mortar mt-0.5">{sub}</p>}
    </div>
  );
}

function RoofAttributes({ a }) {
  const conf = (x) => (x?.confidence ? `${Math.round(x.confidence * 100)}% confidence` : null);
  const condition = a.roofCondition?.length ? a.roofCondition.map((c) => c.value).join(", ") : null;
  const eave = a.eaveHeight?.min != null ? `${a.eaveHeight.min}–${a.eaveHeight.max} ${a.eaveHeight.unit || "ft"}` : null;
  return (
    <div className="card-mtr" data-testid="roof-attributes-card">
      <h2 className="font-bold text-navy text-[17px] mb-1 flex items-center gap-2">
        <Layers size={18} className="text-orange" /> Roof attributes
      </h2>
      <p className="text-[13px] text-mortar mb-4">Property characteristics reported by EagleView aerial imagery.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <AttrTile icon={ShieldCheck} label="Material" value={a.roofMaterial?.value} sub={conf(a.roofMaterial)} testid="attr-material" />
        <AttrTile icon={Gauge} label="Condition" value={condition} sub={conf(a.roofCondition?.[0])} testid="attr-condition" />
        <AttrTile icon={Layers} label="Roof shape" value={a.roofShape?.value} sub={conf(a.roofShape)} testid="attr-shape" />
        <AttrTile icon={Building} label="Stories" value={a.storyCount} testid="attr-stories" />
        <AttrTile icon={Ruler} label="Eave height" value={eave} testid="attr-eave" />
        <AttrTile icon={Home} label="Buildings" value={a.buildingCount} testid="attr-buildings" />
        {a.solarPanel && <AttrTile icon={Sun} label="Solar panels" value={a.solarPanel.value ? "Detected" : "None"} sub={conf(a.solarPanel)} testid="attr-solar" />}
        {a.treeOverhang && a.treeOverhang.value != null && (
          <AttrTile icon={TreePine} label="Tree overhang" value={`${a.treeOverhang.value} ft`} testid="attr-tree" />
        )}
      </div>
    </div>
  );
}
